import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { webhookApi } from '../services/webhookApi';
import type { CreateWebhookRequest, UpdateWebhookRequest } from '../types/webhook';

// Query keys
export const webhooksKeys = {
  all: ['webhooks'] as const,
  lists: () => [...webhooksKeys.all, 'list'] as const,
  list: (projectId: string) => [...webhooksKeys.lists(), projectId] as const,
  details: () => [...webhooksKeys.all, 'detail'] as const,
  detail: (projectId: string, webhookId: string) => [...webhooksKeys.details(), projectId, webhookId] as const,
  deliveries: (projectId: string, webhookId: string) => [...webhooksKeys.all, 'deliveries', projectId, webhookId] as const,
  events: (projectId: string) => [...webhooksKeys.all, 'events', projectId] as const,
};

// ============ Query Hooks ============

export function useWebhooks(projectId: string | undefined) {
  return useQuery({
    queryKey: webhooksKeys.list(projectId!),
    queryFn: async () => {
      const response = await webhookApi.list(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch webhooks');
      }
      return response.data!;
    },
    enabled: !!projectId,
  });
}

export function useWebhook(projectId: string | undefined, webhookId: string | undefined) {
  return useQuery({
    queryKey: webhooksKeys.detail(projectId!, webhookId!),
    queryFn: async () => {
      const response = await webhookApi.get(projectId!, webhookId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch webhook');
      }
      return response.data!;
    },
    enabled: !!projectId && !!webhookId,
  });
}

export function useWebhookDeliveries(
  projectId: string | undefined,
  webhookId: string | undefined,
  params?: { limit?: number; offset?: number; status?: string; event_type?: string }
) {
  return useQuery({
    queryKey: [...webhooksKeys.deliveries(projectId!, webhookId!), params],
    queryFn: async () => {
      const response = await webhookApi.deliveries(projectId!, webhookId!, params);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch deliveries');
      }
      return { deliveries: response.data!, total: response.total || 0 };
    },
    enabled: !!projectId && !!webhookId,
  });
}

export function useWebhookEvents(projectId: string | undefined) {
  return useQuery({
    queryKey: webhooksKeys.events(projectId!),
    queryFn: async () => {
      const response = await webhookApi.events(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch webhook events');
      }
      return response.data!;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour since event types rarely change
  });
}

// ============ Mutation Hooks ============

export function useCreateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: CreateWebhookRequest;
    }) => {
      const response = await webhookApi.create(projectId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create webhook');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: webhooksKeys.list(variables.projectId) });
    },
  });
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      webhookId,
      data,
    }: {
      projectId: string;
      webhookId: string;
      data: UpdateWebhookRequest;
    }) => {
      const response = await webhookApi.update(projectId, webhookId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update webhook');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: webhooksKeys.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: webhooksKeys.detail(variables.projectId, variables.webhookId) });
    },
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      webhookId,
    }: {
      projectId: string;
      webhookId: string;
    }) => {
      const response = await webhookApi.delete(projectId, webhookId);
      if (!response.success) {
        throw new Error('Failed to delete webhook');
      }
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: webhooksKeys.list(variables.projectId) });
    },
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: async ({
      projectId,
      webhookId,
    }: {
      projectId: string;
      webhookId: string;
    }) => {
      const response = await webhookApi.test(projectId, webhookId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to send test webhook');
      }
      return response;
    },
  });
}

export function useRotateWebhookSecret() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      webhookId,
    }: {
      projectId: string;
      webhookId: string;
    }) => {
      const response = await webhookApi.rotateSecret(projectId, webhookId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to rotate secret');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: webhooksKeys.detail(variables.projectId, variables.webhookId) });
    },
  });
}

export function useRetryDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      webhookId,
      deliveryId,
    }: {
      projectId: string;
      webhookId: string;
      deliveryId: string;
    }) => {
      const response = await webhookApi.retryDelivery(projectId, webhookId, deliveryId);
      if (!response.success) {
        throw new Error('Failed to retry delivery');
      }
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: webhooksKeys.deliveries(variables.projectId, variables.webhookId) });
    },
  });
}
